"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Auto-refresh for the installed web app (Add to Home Screen).
 *
 * iOS standalone mode has no reload button and no pull-to-refresh, so a
 * suspended app can come back showing a stale page for days. On foreground:
 *
 *  - after ≥5 min hidden, check the deployed build fingerprint
 *    (<meta name="prepain-build">, inc/app/app-manifest.php) against the live
 *    /prepain-app-version.json endpoint and reload immediately if a deploy
 *    changed it — new code reaches suspended phones within minutes; and
 *  - after ≥30 min hidden, reload even when the build is unchanged, so the
 *    server-rendered data baked into the page (streak/racha count, "meta hoy",
 *    course progress, the unread-boletín badge) is refreshed rather than
 *    showing yesterday's numbers on the first screen.
 *
 * Between 5 and 30 min with no deploy nothing happens, so quick task-switches
 * don't flash a reload. If the version check can't run (offline/flaky/meta
 * missing) only the ≥30 min blind reload applies. Quiz/media guards always win.
 */
class AppRefresh {
    constructor() {
        /** Run the version check after this long in the background. */
        this.checkAfterMs = 5 * 60 * 1000;
        /** Reload after this long even when the build is unchanged (data refresh). */
        this.blindAfterMs = 30 * 60 * 1000;
        this.versionUrl = '/prepain-app-version.json';
        this.hiddenAt = null;
    }
    init() {
        if (!this.isStandalone()) {
            return;
        }
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.hiddenAt = Date.now();
                return;
            }
            this.updateServiceWorker();
            this.maybeReload();
        });
        // bfcache restore (back navigation) counts as coming back too.
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.maybeReload();
            }
        });
    }
    isStandalone() {
        return (navigator.standalone === true ||
            window.matchMedia('(display-mode: standalone)').matches);
    }
    maybeReload() {
        if (this.hiddenAt === null) {
            return;
        }
        const elapsed = Date.now() - this.hiddenAt;
        if (elapsed < this.checkAfterMs) {
            return;
        }
        // One attempt per background episode: a reload blocked by a quiz or by a
        // fresh build is deliberately not retried until the app hides again.
        this.hiddenAt = null;
        if (!this.isSafeToReload()) {
            return;
        }
        const longIdle = elapsed >= this.blindAfterMs;
        const pageBuild = this.pageBuild();
        if (!pageBuild) {
            // No fingerprint to diff — purely time-based: blind reload once idle.
            if (longIdle) {
                window.location.reload();
            }
            return;
        }
        this.fetchServerBuild().then((serverBuild) => {
            // serverBuild === null means the check failed (offline/flaky); fall back
            // to the long-idle reload only. Otherwise reload when the deployed build
            // changed (fast path) OR after a long background (refresh stale data).
            const buildChanged = serverBuild !== null && serverBuild !== pageBuild;
            if ((buildChanged || longIdle) && this.isSafeToReload()) {
                window.location.reload();
            }
        });
    }
    pageBuild() {
        const meta = document.querySelector('meta[name="prepain-build"]');
        return meta && meta.content ? meta.content : null;
    }
    fetchServerBuild() {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 5000);
        // Cache-bust the query string: the endpoint sends no-store, but the CDN
        // (Cloudflare) overrides it to max-age=600 and serves a cached build id for
        // up to 10 min — which would delay deploy detection. A unique param forces
        // an edge cache miss so we always read the live build. `cache: 'no-store'`
        // covers the browser's own HTTP cache.
        const url = this.versionUrl + '?_=' + Date.now();
        return fetch(url, { cache: 'no-store', signal: controller.signal })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => data && typeof data.build === 'string' ? data.build : null)
            .catch(() => null)
            .finally(() => window.clearTimeout(timer));
    }
    /**
     * Re-check /app-sw.js on every foreground so a deploy — or the SW kill
     * switch — reaches the installed app without waiting for a navigation.
     */
    updateServiceWorker() {
        if (!('serviceWorker' in navigator)) {
            return;
        }
        navigator.serviceWorker
            .getRegistration('/')
            .then((registration) => registration && registration.update())
            .catch(() => { });
    }
    /** Never interrupt a quiz in progress or media that is playing. */
    isSafeToReload() {
        if (document.body.classList.contains('single-sfwd-quiz')) {
            return false;
        }
        if (document.querySelector('.wpProQuiz_quiz:not([style*="display: none"]) .wpProQuiz_questionList')) {
            return false;
        }
        const media = document.querySelectorAll('video, audio');
        const playing = Array.from(media).some((element) => !element.paused && !element.ended);
        return !playing;
    }
}
exports.default = AppRefresh;
